'use client'

import { memo } from 'react'
import { Badge } from '@/components/ui/badge'
import { Check } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'

export interface ComparisonRow {
  dimension: string
  ninxl: string
  codex: string
  perplexity: string
  chatgpt: string
  claudeDesktop: string
  /** NINX has a clear advantage on this dimension */
  ninxlWins?: boolean
}

/** baby: Short copy so the compare block stays scannable, not a wall of text. */
export const NINX_VS_OTHERS_ROWS: ComparisonRow[] = [
  {
    dimension: 'Focus',
    ninxl: 'Private desktop agent',
    codex: 'Code / IDE',
    perplexity: 'AI search',
    chatgpt: 'General chat',
    claudeDesktop: 'Reasoning & artifacts',
  },
  {
    dimension: 'Privacy',
    ninxl: 'Local-first by default',
    codex: 'Mostly cloud',
    perplexity: 'Cloud',
    chatgpt: 'Cloud',
    claudeDesktop: 'Cloud',
    ninxlWins: true,
  },
  {
    dimension: 'Data retention',
    ninxl: 'On-device local · cloud ZDR',
    codex: 'Cloud retention',
    perplexity: 'Cloud retention',
    chatgpt: 'Cloud retention',
    claudeDesktop: 'Cloud retention',
    ninxlWins: true,
  },
  {
    dimension: 'On-device AI',
    ninxl: 'GGUF + BYOK',
    codex: 'Limited',
    perplexity: 'No',
    chatgpt: 'No',
    claudeDesktop: 'No',
    ninxlWins: true,
  },
  {
    dimension: 'Offline',
    ninxl: 'Chat, tools, voice',
    codex: 'Partial',
    perplexity: 'Needs net',
    chatgpt: 'Needs net',
    claudeDesktop: 'Needs net',
    ninxlWins: true,
  },
  {
    dimension: 'Voice mode',
    ninxl: 'On-device voice agent',
    codex: 'None',
    perplexity: 'Cloud voice',
    chatgpt: 'Cloud voice',
    claudeDesktop: 'Limited cloud',
    ninxlWins: true,
  },
  {
    dimension: 'InkFlow',
    ninxl: 'System-wide dictate · instruct · transform',
    codex: 'None',
    perplexity: 'None',
    chatgpt: 'In-app only',
    claudeDesktop: 'In-app only',
    ninxlWins: true,
  },
  {
    dimension: 'Tools',
    ninxl: 'Browser, shell, email, brokers',
    codex: 'Code & files',
    perplexity: 'Search',
    chatgpt: 'Browse, GPTs',
    claudeDesktop: 'Computer use',
    ninxlWins: true,
  },
  {
    dimension: 'Rooms + compute',
    ninxl: 'Chat rooms + share local compute mesh',
    codex: 'None',
    perplexity: 'None',
    chatgpt: 'Team workspace (cloud)',
    claudeDesktop: 'None',
    ninxlWins: true,
  },
  {
    dimension: 'Size',
    ninxl: '~20 MB, fast start',
    codex: 'IDE-sized',
    perplexity: 'Light app',
    chatgpt: 'Light app',
    claudeDesktop: 'Light app',
    ninxlWins: true,
  },
  {
    dimension: 'Best for',
    ninxl: 'Private daily driver',
    codex: 'Dev workflows',
    perplexity: 'Research',
    chatgpt: 'Everyday chat',
    claudeDesktop: 'Deep reasoning',
  },
]

/** @deprecated use NINX_VS_OTHERS_ROWS */
export const NINXL_VS_OTHERS_ROWS = NINX_VS_OTHERS_ROWS

export const NinxlComparisonTable = memo(function NinxlComparisonTable({
  rows = NINX_VS_OTHERS_ROWS,
  className,
}: {
  rows?: ComparisonRow[]
  className?: string
}) {
  const winCount = rows.filter((r) => r.ninxlWins).length

  return (
    <div className={cn('mx-auto max-w-4xl space-y-3', className)}>
      <div className="flex flex-wrap items-center justify-center gap-2 rounded-xl border border-[#21728d]/12 bg-[#21728d]/[0.04] px-3 py-2">
        <span className="text-xs font-semibold text-[#134b5f]">
          NINX leads on {winCount} of {rows.length}
        </span>
        <div className="flex gap-1">
          {rows.map((row) => (
            <div
              key={row.dimension}
              className={cn(
                'h-1.5 w-1.5 rounded-full',
                row.ninxlWins ? 'bg-[#21728d]' : 'bg-[#88a1ac]/35',
              )}
              title={row.dimension}
            />
          ))}
        </div>
      </div>

      <div className="ninxl-comparison-table overflow-x-auto rounded-2xl border border-slate-200/90 bg-white/90 shadow-sm shadow-slate-200/40">
        <Table className="min-w-[640px] text-xs">
          <TableHeader>
            <TableRow className="hover:bg-transparent border-b border-slate-100">
              <TableHead className="w-[14%] py-2.5 pl-3 text-[10px] font-medium uppercase tracking-wide text-slate-400">
                Dimension
              </TableHead>
              <TableHead className="w-[22%] py-2.5 ninxl-comparison-col-highlight">
                <span className="inline-flex items-center gap-1.5 font-semibold text-[#134b5f]">
                  NINX
                  <Badge className="border-0 bg-[#21728d] px-1.5 py-0 text-[9px] tracking-wide text-white">
                    REC
                  </Badge>
                </span>
              </TableHead>
              <TableHead className="w-[16%] py-2.5 text-[10px] font-medium text-slate-400">Codex</TableHead>
              <TableHead className="w-[16%] py-2.5 text-[10px] font-medium text-slate-400">Perplexity</TableHead>
              <TableHead className="w-[16%] py-2.5 text-[10px] font-medium text-slate-400">ChatGPT</TableHead>
              <TableHead className="w-[16%] py-2.5 pr-3 text-[10px] font-medium text-slate-400">
                Claude Desktop
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, idx) => (
              <TableRow
                key={row.dimension}
                className={cn(
                  'border-b border-slate-50 transition-colors hover:bg-[#f6f9fd]/50',
                  idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/40',
                )}
              >
                <TableCell className="py-2 pl-3 text-[11px] font-medium text-slate-700">
                  {row.dimension}
                </TableCell>
                <TableCell className="py-2 pr-1 text-[11px] font-medium text-slate-800 ninxl-comparison-col-highlight">
                  <span className="inline-flex items-start gap-1">
                    {row.ninxlWins ? (
                      <Check className="mt-0.5 h-3 w-3 shrink-0 text-[#21728d]" aria-hidden />
                    ) : null}
                    {row.ninxl}
                  </span>
                </TableCell>
                <TableCell className="py-2 pr-1 text-[11px] text-slate-500">{row.codex}</TableCell>
                <TableCell className="py-2 pr-1 text-[11px] text-slate-500">{row.perplexity}</TableCell>
                <TableCell className="py-2 pr-1 text-[11px] text-slate-500">{row.chatgpt}</TableCell>
                <TableCell className="py-2 pr-3 text-[11px] text-slate-500">{row.claudeDesktop}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
})
